import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ticketsApi } from '../api/tickets.api'
import { ticketsKeys } from './query-keys'

export interface SyncJobState {
	pendingJobId: string | null
	jobStatus?: { status?: string; error?: string | null }
	triggerPending: boolean
	trigger: () => void
}

/**
 * Drives the admin-only "Sync now" affordance — fires `POST /tickets/sync`
 * via the api layer and polls `/tickets/sync/:jobId` every 2s until the
 * backend reports a terminal status. The caller's `onSettled` callback fires
 * exactly once per job and is used by `AdminTicketsPage` to invalidate the
 * list query after a successful run.
 */
export function useSyncJob(onSettled: () => void): SyncJobState {
	const [pendingJobId, setPendingJobId] = useState<string | null>(null)
	const triggerMutation = useMutation({
		mutationFn: () => ticketsApi.trigger(),
		onSuccess: (job) => setPendingJobId(job.jobId),
	})
	const { data: jobStatus } = useQuery({
		queryKey: ticketsKeys.syncJob(pendingJobId),
		queryFn: () => ticketsApi.getStatus(pendingJobId ?? ''),
		enabled: !!pendingJobId,
		refetchInterval: (query) => {
			const last = query.state.data
			return last && (last.status === 'completed' || last.status === 'failed') ? false : 2000
		},
	})
	if (jobStatus?.status === 'completed' || jobStatus?.status === 'failed') {
		if (pendingJobId === jobStatus.jobId) onSettled()
	}
	return {
		pendingJobId,
		jobStatus: jobStatus ? { status: jobStatus.status, error: jobStatus.error ?? null } : undefined,
		triggerPending: triggerMutation.isPending,
		trigger: () => triggerMutation.mutate(),
	}
}
